<template>
  <div class="home">
    <!-- ============================= HERO ============================= -->
    <section class="hero">
      <div class="hero-glow" aria-hidden="true"></div>
      <div class="lp-container hero-inner">
        <div class="lp-eyebrow hero-eyebrow">
          <span class="lp-eyebrow__dot"></span>
          {{ $t('hero.badge') }}
        </div>

        <h1 class="lp-h1 hero-title lp-balance">
          {{ $t('hero.title1') }} {{ $t('hero.title2') }}
          <span class="lp-accent-text">{{ $t('hero.titleHighlight') }}</span>
        </h1>

        <p class="lp-lead hero-lead lp-balance">{{ $t('hero.subtitle') }}</p>

        <div class="hero-actions">
          <router-link class="lp-btn lp-btn--primary lp-btn--lg" to="/download">
            <l-icon small color="white">mdi-download</l-icon>
            {{ $t('hero.cta') }}
          </router-link>
          <a class="lp-btn lp-btn--ghost lp-btn--lg" :href="webAppUrl">
            <l-icon small>globe</l-icon>
            {{ $t('hero.ctaWeb') }}
          </a>
        </div>

        <p class="hero-trust">
          <l-icon size="15">mdi-shield-check</l-icon>
          {{ $t('hero.trust') }}
        </p>

        <p class="hero-platforms">{{ $t('hero.platforms') }}</p>

        <!-- Product screenshot, above the fold -->
        <div class="hero-shot lp-frame">
          <app-screenshot variant="inbox" :label="$t('showcase.shots.inbox')"></app-screenshot>
        </div>
      </div>
    </section>

    <!-- ===================== COMPATIBILITY STRIP ===================== -->
    <section class="logos lp-container">
      <p class="logos-label">{{ $t('hero.worksWith') }}</p>
      <div class="logos-row">
        <span class="logo-item">
          <svg class="logo-icon" viewBox="0 -31.5 256 256" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path fill-opacity="0.6" d="M58.1818182,192.049515 L58.1818182,93.1404244 L27.5066233,65.0770089 L0,49.5040608 L0,174.59497 C0,184.253152 7.82545455,192.049515 17.4545455,192.049515 L58.1818182,192.049515 Z" />
            <path fill-opacity="0.48" d="M197.818182,192.049515 L238.545455,192.049515 C248.203636,192.049515 256,184.224061 256,174.59497 L256,49.5040608 L224.844415,67.3422767 L197.818182,93.1404244 L197.818182,192.049515 Z" />
            <polygon fill-opacity="1" points="58.1818182 93.1404244 54.0077618 54.4932827 58.1818182 17.5040608 128 69.8676972 197.818182 17.5040608 202.487488 52.4960089 197.818182 93.1404244 128 145.504061" />
            <path fill-opacity="0.35" d="M197.818182,17.5040608 L197.818182,93.1404244 L256,49.5040608 L256,26.2313335 C256,4.64587897 231.36,-7.65957557 214.109091,5.28587897 L197.818182,17.5040608 Z" />
            <path fill-opacity="0.82" d="M0,49.5040608 L26.7588051,69.5731646 L58.1818182,93.1404244 L58.1818182,17.5040608 L41.8909091,5.28587897 C24.6109091,-7.65957557 0,4.64587897 0,26.2313335 L0,49.5040608 Z" />
          </svg>Gmail
        </span>
        <span class="logo-item">
          <svg class="logo-icon" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M19.484,7.937v5.477L21.4,14.619a.489.489,0,0,0,.21,0l8.238-5.554a1.174,1.174,0,0,0-.959-1.128Z" />
            <path d="M19.484,15.457l1.747,1.2a.522.522,0,0,0,.543,0c-.3.181,8.073-5.378,8.073-5.378V21.345a1.408,1.408,0,0,1-1.49,1.555H19.483V15.457Z" />
            <path d="M10.44,12.932a1.609,1.609,0,0,0-1.42.838,4.131,4.131,0,0,0-.526,2.218A4.05,4.05,0,0,0,9.02,18.2a1.6,1.6,0,0,0,2.771.022,4.014,4.014,0,0,0,.515-2.2,4.369,4.369,0,0,0-.5-2.281A1.536,1.536,0,0,0,10.44,12.932Z" />
            <path d="M2.153,5.155V26.582L18.453,30V2ZM13.061,19.491a3.231,3.231,0,0,1-2.7,1.361,3.19,3.19,0,0,1-2.64-1.318A5.459,5.459,0,0,1,6.706,16.1a5.868,5.868,0,0,1,1.036-3.616A3.267,3.267,0,0,1,10.486,11.1a3.116,3.116,0,0,1,2.61,1.321,5.639,5.639,0,0,1,1,3.484A5.763,5.763,0,0,1,13.061,19.491Z" />
          </svg>Outlook
        </span>
        <span class="logo-item">
          <svg class="logo-icon" viewBox="0 0 80 52" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <defs>
              <linearGradient id="icloud-gray" x1="6" y1="6" x2="74" y2="50" gradientUnits="userSpaceOnUse">
                <stop offset="0" stop-color="currentColor" stop-opacity="1" />
                <stop offset="0.55" stop-color="currentColor" stop-opacity="0.7" />
                <stop offset="1" stop-color="currentColor" stop-opacity="0.42" />
              </linearGradient>
            </defs>
            <path fill="url(#icloud-gray)" d="M 45.864371,0.751258 A 21.518704,21.518704 0 0 0 27.127725,11.764594 11.804325,11.804325 0 0 0 21.976186,10.573131 11.804325,11.804325 0 0 0 10.35494,20.48888 16.255137,16.255137 0 0 0 0.37795502,35.481798 16.255137,16.255137 0 0 0 16.640956,51.721206 16.255137,16.255137 0 0 0 18.679799,51.57426 h 45.187983 a 15.130063,15.202083 0 0 0 0.712824,0.03447 15.130063,15.202083 0 0 0 0.679264,-0.03447 h 1.082164 V 51.49383 A 15.130063,15.202083 0 0 0 79.703385,36.406573 V 36.3774 A 15.130063,15.202083 0 0 0 67.386251,21.477308 21.518704,21.518704 0 0 0 45.864371,0.751258 Z" />
          </svg>iCloud
        </span>
        <span class="logo-item">
          <svg class="logo-icon logo-icon--imap" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <rect x="2.5" y="3.5" width="19" height="7" rx="1.8" fill-opacity="0.55" />
            <rect x="2.5" y="13.5" width="19" height="7" rx="1.8" fill-opacity="0.42" />
            <rect x="8.5" y="5.9" width="9.5" height="2.2" rx="1.1" fill-opacity="0.85" />
            <rect x="8.5" y="15.9" width="9.5" height="2.2" rx="1.1" fill-opacity="0.7" />
            <circle cx="5.7" cy="7" r="1.15" fill-opacity="1" />
            <circle cx="5.7" cy="17" r="1.15" fill-opacity="1" />
          </svg>IMAP
        </span>
      </div>
    </section>

    <hr class="lp-hr" />

    <!-- ========================== FEATURES ========================== -->
    <features-section id="features"></features-section>

    <hr class="lp-hr" />

    <!-- ========================== SECURITY ========================== -->
    <security-section id="security"></security-section>

    <hr class="lp-hr" />

    <!-- =========================== PRICING ========================== -->
    <pricing-section id="pricing"></pricing-section>

    <hr class="lp-hr" />

    <!-- ======================== TESTIMONIALS ======================== -->
    <testimonials-section id="testimonials"></testimonials-section>

    <!-- ========================= FINAL CTA ========================= -->
    <section class="final-cta">
      <div class="lp-container final-cta__inner">
        <h2 class="lp-h2 lp-balance">
          {{ $t('cta.title1') }}
          <span class="lp-accent-text">{{ $t('cta.titleHighlight') }}</span>
        </h2>
        <p class="lp-lead final-cta__lead lp-balance">{{ $t('cta.subtitle') }}</p>
        <div class="hero-actions">
          <router-link class="lp-btn lp-btn--primary lp-btn--lg" to="/download">
            <l-icon small color="white">mdi-download</l-icon>
            {{ $t('cta.button') }}
          </router-link>
          <a class="lp-btn lp-btn--ghost lp-btn--lg" :href="webAppUrl">
            <l-icon small>globe</l-icon>
            {{ $t('hero.ctaWeb') }}
          </a>
        </div>
        <p class="hero-trust">{{ $t('download.requirements') }}</p>
      </div>
    </section>
  </div>
</template>

<script>
import FeaturesSection from '@/components/features/FeaturesSection.vue';
import SecuritySection from '@/components/security/SecuritySection.vue';
import PricingSection from '@/components/pricing/PricingSection.vue';
import TestimonialsSection from '@/components/testimonials/TestimonialsSection.vue';
import AppScreenshot from '@/components/mockup/AppScreenshot.vue';
import { WEB_APP_URL } from '@/constants/download';
import { SUPPORTED } from '@/i18n';

const SITE = 'https://nukliapp.com';

export default {
  name: 'HomePage',
  metaInfo() {
    const locale = this.$i18n.locale;
    const canonical = SITE + (locale === 'en' ? '/' : `/${locale}`);
    const alternates = SUPPORTED.map((code) => ({
      rel: 'alternate', hreflang: code, href: SITE + (code === 'en' ? '/' : `/${code}`),
    }));
    alternates.push({ rel: 'alternate', hreflang: 'x-default', href: `${SITE}/` });
    return {
      title: this.$t('meta.title'),
      titleTemplate: '%s',
      htmlAttrs: { lang: locale },
      meta: [
        { name: 'description', content: this.$t('meta.description') },
        { property: 'og:title', content: this.$t('meta.title') },
        { property: 'og:description', content: this.$t('meta.description') },
        { property: 'og:url', content: canonical },
        { property: 'og:locale', content: locale },
        { property: 'og:type', content: 'website' },
        { property: 'twitter:card', content: 'summary_large_image' },
      ],
      link: [{ rel: 'canonical', href: canonical }, ...alternates],
    };
  },
  components: {
    FeaturesSection,
    SecuritySection,
    PricingSection,
    TestimonialsSection,
    AppScreenshot,
  },
  data() {
    return {
      webAppUrl: WEB_APP_URL,
    };
  },
};
</script>

<style scoped>
.home {
  background: var(--lp-bg);
}

/* ---- Hero ---- */
.hero {
  position: relative;
  /* 64px del AppBar fijo + aire, como el resto de páginas */
  padding-top: 128px;
  padding-bottom: 0;
  overflow: hidden;
}
.hero-glow {
  position: absolute;
  top: -160px;
  left: 50%;
  transform: translateX(-50%);
  width: 1100px;
  height: 560px;
  background: radial-gradient(60% 60% at 50% 0%, var(--lp-accent-weak) 0%, rgba(0, 0, 0, 0) 70%);
  pointer-events: none;
  z-index: 0;
}
.hero-inner {
  position: relative;
  z-index: 1;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.hero-eyebrow { margin-bottom: 22px; }
.hero-title {
  max-width: 16ch;
  margin-bottom: 24px;
}
.hero-title .lp-accent-text { display: block; }
.hero-lead {
  max-width: 600px;
  margin-bottom: 36px;
}
.hero-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: center;
}
.hero-trust {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin-top: 20px;
  font-size: 0.875rem;
  color: var(--lp-muted);
}
.hero-platforms {
  margin-top: 8px;
  font-size: 0.8125rem;
  color: var(--lp-muted);
  opacity: 0.85;
}
.hero-shot {
  margin-top: 64px;
  margin-bottom: 64px;
  width: 100%;
  max-width: 1020px;
}

/* ---- Compatibility strip ---- */
.logos {
  padding: 56px 24px;
  text-align: center;
  margin-top: -64px;
}
.logos-label {
  font-size: 0.8125rem;
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--lp-muted);
  margin-bottom: 22px;
}
.logos-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 40px;
}
.logo-item {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--lp-muted);
}
.logo-icon {
  width: 22px;
  height: 22px;
  fill: currentColor;
  flex-shrink: 0;
}
.logo-icon--imap {
  width: 24px;
  height: 24px;
}

/* ---- Final CTA ---- */
.final-cta {
  padding: 120px 0;
  background: var(--lp-bg-subtle);
  border-top: 1px solid var(--lp-line);
  text-align: center;
}
.final-cta__inner {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.final-cta__lead {
  max-width: 520px;
  margin: 20px 0 32px;
}

@media (max-width: 599px) {
  .hero { padding-top: 104px; }
  .hero-shot { margin-top: 44px; }
  .logos-row { gap: 24px; }
  .final-cta { padding: 80px 0; }
}
</style>
